import { default as productsConstants } from '@utils/constants/product.js';
import { compareOrdersByReservationStatus } from '@utils/hotelDetailsUtils';
import currencySimpleUtility from '@utils/currencySimple';
import dateUtils from './dateUtils';
import { commissionConstants } from './constants';
import dateShortFormat from '@/utils/dateShortFormat';
import dateTimeLongFormat from '@/utils/dateTimeLongFormat';
import percentFormat from '@/utils/percentFormat';
import loyaltyPointsFormat from '@/utils/loyaltyPointsFormat';
import ticketDetailsUtility from '@/utils/ticketDetails';
import currencyFullUtility from '@/utils/currencyFull';
import statusCustomerTranslation from '@/utils/statusCustomerTranslation';

export function normalizeCustomer(data) {
    return {
        id: data.id,
        partnerId: data.partnerId,
        partner: data.partner || '-',
        email: data.email || '-',
        firstName: data.firstName || '-',
        lastName: data.lastName || '-',
        createdAt: data.createdAt
            ? dateShortFormat(dateUtils.parseDateString(data.createdAt))
            : null,
        status: data.status ? statusCustomerTranslation(data.status) : '-',
        loyaltyNumber: data.accountNumber || '-',
        isEditable: data.editable || null,
    };
}

function normalizeLocation(location) {
    const ret = { country: null, state: null, city: null };

    if (location.country) {
        ret.country = {
            code: location.country,
            name: location.countryName || location.country,
        };
    }
    if (location.state) {
        ret.state = {
            code: location.state,
            name: location.stateName || location.state,
        };
    }
    if (location.city) {
        ret.city = {
            name: location.city,
        };
    }

    return ret;
}

export function normalizeHotelPriceRule(data) {
    return {
        id: data.id,
        name: data.name || '-',
        partnerIDs: data.partnerIDs,
        status: data.status,
        from: data.from ? dateUtils.parseDateString(data.from) : null,
        to: data.to ? dateUtils.parseDateString(data.to) : null,
        activationDate: data.activationDate ? dateUtils.parseDateString(data.activationDate) : null,
        location: normalizeLocation(data.location),
        type: data.ruleType || '',
        action: data.action || null,
        description: data.description || '-',
    };
}

export function normalizeLoyaltyTransactions(data) {
    return {
        baseCurrencyType: data.baseCurrencyType,
        createdAt: data.createdAt
            ? dateShortFormat(dateUtils.parseDateString(data.createdAt))
            : null,
        loyaltyBaseCurrency: data.loyaltyBaseCurrency,
        loyaltyBaseCurrencyType: data.loyaltyBaseCurrencyType,
        loyaltyCurrencyName: data.loyaltyCurrencyName,
        id: data.id,
        orderId: data.orderId,
        programName: data.programName,
        status: data.status,
        type: data.type,
        loyaltyCurrency: data.baseCurrencyType,
        loyaltyPointsEarnOrBurn: loyaltyPointsFormat(
            data.amount,
            data.loyaltyBaseCurrencyType,
            data.loyaltyBaseCurrency,
            false
        ),
        ruleIds: data.ruleIds && data.ruleIds.length > 0 ? data.ruleIds.join(' ') : '',
    };
}

export function normalizeOrdersListOneOrder(data) {
    return (
        data?.items && {
            id: data.id,
            cashPayment: currencySimpleUtility(data.cashPayment || 0, data.currency),
            totalPrice: currencySimpleUtility(data.totalPrice || 0, data.currency),
            currency: data.currency,
            createdAt: data.createdAt
                ? dateShortFormat(dateUtils.parseDateString(data.createdAt))
                : '',
            orderStatus: data.orderStatus ? data.orderStatus : '-',
            orderNumber: data.items[0]?.orderNumber || '-',
            redemptionUrl: data.items[0]?.redemptionUrl || '',
            paymentStatus: data.paymentStatus ? data.paymentStatus : '-',
            packageCode: data.packages.length > 0 ? data.packages[0].code : '',
            packageName: data.packages.length > 0 ? data.packages[0].name : '',
            loyaltyCurrencyType: data.loyaltyCurrency,
            loyaltyPointsBurned: loyaltyPointsFormat(
                data.pointsPayment || 0,
                data.loyaltyCurrency,
                data.currency,
                false
            ),
            loyaltyPointsEarned:
                data.loyaltyCurrency.baseCurrencyType === 'MONEY'
                    ? currencyFullUtility(data.pointsEarned || 0, data.loyaltyCurrency.baseCurrency)
                    : data.pointsEarned,
            items: data.items.reduce((ret, item) => {
                return [
                    ...ret,
                    ...normalizeOrdersListOneOrderItems(item, data.currency, data.orderStatus),
                ];
            }, []),
        }
    );
}

export function normalizeOrdersListOneOrderItems(data, currency, orderStatus) {
    switch (data.productTypeId) {
        case productsConstants.TYPE.EVENT:
            return normalizeOrdersListOneOrderItemEventTicket(data, currency, orderStatus);
        case productsConstants.TYPE.HOTEL:
            return normalizeOrdersListOneOrderItemHotelReservation(data, currency, orderStatus);
        default:
            return data;
    }
}

export function normalizeOrdersListOneOrderItemEventTicket(data, currency) {
    return data.items.map((item) => {
        return {
            amount: currencySimpleUtility(item.totalNetPrice || 0, currency),
            basePrice: currencySimpleUtility(item.price || 0, currency),
            currency: currency || '',
            description: item?.description || '',
            fees: currencySimpleUtility(item.fees || 0, currency),
            loyaltyCurrency: data.loyaltyCurrency,
            name: item?.name || '',
            orderStatus: data.status ? data.status : '-',
            quantity: item?.quantity || 0,
            row: item?.row || '',
            seats: item?.seats || '',
            section: item?.section || '',
            supplier: data.supplier || '',
            totalAmount: currencySimpleUtility(item.totalPrice || 0, currency),
            productTypeId: data.productTypeId,
            ticketType: item?.name || '',
            ticketTypeId: item?.rateId || '',
            inventoryType: item?.inventory || '',
        };
    });
}

export function normalizeOrdersListOneOrderItemHotelReservation(data, currency) {
    return data.items.map((item) => {
        return {
            orderStatus: data.status ? data.status : '-',
            orderNumber: data.orderNumber ? data.orderNumber : '-',
            roomId: item?.roomId || '-',
            roomRateId: item?.rateId || 0,
            adultCount: item?.adultCount || 0,
            childCount: item?.childCount || 0,
            childAge: item?.childAge || 0,
            partnerPrice: currencySimpleUtility(item?.partnerPrice || 0, currency),
            remarks: data.remarks || '-',
            productTypeId: data.productTypeId,
        };
    });
}

export function normalizeOrdersList(data) {
    return {
        ...data,
        orderStatus: data.orderStatus ? data.orderStatus.toUpperCase() : '-',
        paymentStatus: data.paymentStatus ? data.paymentStatus.toUpperCase() : '-',
        amountCash: currencySimpleUtility(data.cashPayment || 0, data.currency),
        loyaltyPointsBurned: loyaltyPointsFormat(
            data.pointsPayment || 0,
            data.loyaltyBaseCurrencyType,
            data.loyaltyBaseCurrency,
            false
        ),
        loyaltyPointsEarned: loyaltyPointsFormat(
            data.pointsEarned || 0,
            data.loyaltyBaseCurrencyType,
            data.loyaltyBaseCurrency,
            false
        ),
        transactionIds:
            data.transactionIds && data.transactionIds.length > 0
                ? data.transactionIds.join('/')
                : '',
        totalAmount: currencySimpleUtility(data.totalPrice, data.currency),
        totalTaxes: currencySimpleUtility(data.totalTaxes || 0, data.currency),
        loyaltyBaseCurrency: data.loyaltyBaseCurrency,
        loyaltyBaseCurrencyType: data.loyaltyBaseCurrencyType,
        loyaltyCurrencyName: data.loyaltyCurrencyName,
        channel: data.channel,
    };
}

export function normalizeReportOrdersList(data) {
    return {
        amountCash: data.cashPayment,
        createdAt: data.aggregationDate,
        currency: data.currency,
        loyaltyCurrency: data.loyaltyCurrency,
        loyaltyPointsBurned: data.pointsPayment || 0,
        loyaltyPointsEarned: data.pointsEarned || 0,
        partnerName: data.partnerName || '-',
        tenantName: data.tenantName || '-',
        productType: data.productType || '-',
        totalAmount: data.totalPrice,
        totalOrders: data.totalOrders || 0,
        commissionPrimary: data.commissionPrimary || 0,
        commissionSecondary: data.commissionSecondary || 0,
        commissionPresale: data.commissionPresale || 0,
        totalTaxes: data.totalTaxes,
        loyaltyBaseCurrency: data.loyaltyBaseCurrency,
        loyaltyBaseCurrencyType: data.loyaltyBaseCurrencyType,
        loyaltyCurrencyName: data.loyaltyCurrencyName,
        loyaltyPointsEarnedRenderer: loyaltyPointsFormat(
            data.pointsEarned || 0,
            data.loyaltyBaseCurrencyType,
            data.loyaltyBaseCurrency,
            false
        ),
        loyaltyPointsBurnedRenderer: loyaltyPointsFormat(
            data.pointsPayment || 0,
            data.loyaltyBaseCurrencyType,
            data.loyaltyBaseCurrency,
            false
        ),
        amountCashRenderer: currencySimpleUtility(data.cashPayment || 0, data.currency),
        totalAmountRenderer: currencySimpleUtility(data.totalPrice || 0, data.currency),
        totalTaxesRenderer: currencySimpleUtility(data.totalTaxes || 0, data.currency),
        commissionPrimaryRenderer: currencySimpleUtility(
            data.commissionPrimary || 0,
            commissionConstants.CURRENCY
        ),
        commissionSecondaryRenderer: currencySimpleUtility(
            data.commissionSecondary || 0,
            commissionConstants.CURRENCY
        ),
        commissionPresaleRenderer: currencySimpleUtility(
            data.commissionPresale || 0,
            commissionConstants.CURRENCY
        ),
    };
}

export function normalizeReportOrdersListTotals(data) {
    if (!data) return null;

    return data.map((total) => ({
        currency: total.currency,
        totalOrders: total.totalOrders,
        totalAmount: total.totalPrice,
        totalTaxes: total.totalTaxes,
        amountCash: total.cashPayment || 0,
        loyaltyPointsBurned: total.pointsPayment,
        loyaltyPointsEarned: total.pointsEarned,
        loyaltyBaseCurrency: total.loyaltyBaseCurrency,
        loyaltyBaseCurrencyType: total.loyaltyBaseCurrencyType,
        loyaltyCurrencyName: total.loyaltyCurrencyName,
        commissionPrimary: currencySimpleUtility(
            total.commissionPrimary || 0,
            commissionConstants.CURRENCY
        ),
        commissionSecondary: currencySimpleUtility(
            total.commissionSecondary || 0,
            commissionConstants.CURRENCY
        ),
        commissionPresale: currencySimpleUtility(
            total.commissionPresale || 0,
            commissionConstants.CURRENCY
        ),
    }));
}

export function normalizeReportFinanceCommissionItem(data) {
    let newReportFinanceCommission = [];

    newReportFinanceCommission.push({
        partnerId: data.partnerId,
        partnerName: data.partnerName || '-',
        status: '',
        primaryTicketsCount: '',
        secondaryTicketsCount: '',
        preSalesTicketsCount: '',
        totalPrice: data.totalPrice || 0,
        totalPriceCommissioned: data.totalPriceCommissioned || 0,
        primaryCommission: '',
        secondaryCommission: '',
        preSalesCommission: '',
        totalCommission: '',
        partnerNameSplitted: '',
        totalPriceRenderer: currencySimpleUtility(
            data.totalPrice || 0,
            commissionConstants.CURRENCY
        ),
        primaryCommissionRenderer: '',
        secondaryCommissionRenderer: '',
        preSalesCommissionRenderer: '',
        totalCommissionRenderer: '',
    });

    data.items.forEach((itemStatus) => {
        newReportFinanceCommission.push({
            partnerName: '',
            status: itemStatus.status,
            primaryTicketsCount: itemStatus.primaryTicketsCount || 0,
            secondaryTicketsCount: itemStatus.secondaryTicketsCount || 0,
            preSalesTicketsCount: itemStatus.preSalesTicketsCount || 0,
            totalPrice: itemStatus.totalPrice || '',
            totalPriceCommissioned: itemStatus.totalPriceCommissioned || 0,
            primaryCommission: itemStatus.primaryCommission || 0,
            secondaryCommission: itemStatus.secondaryCommission || 0,
            preSalesCommission: itemStatus.preSalesCommission || 0,
            totalCommission: itemStatus.totalCommission || 0,
            partnerNameSplitted: '',
            totalPriceRenderer: currencySimpleUtility(
                itemStatus.totalPrice || 0,
                commissionConstants.CURRENCY
            ),
            primaryCommissionRenderer: currencySimpleUtility(
                itemStatus.primaryCommission || 0,
                commissionConstants.CURRENCY
            ),
            secondaryCommissionRenderer: currencySimpleUtility(
                itemStatus.secondaryCommission || 0,
                commissionConstants.CURRENCY
            ),
            preSalesCommissionRenderer: currencySimpleUtility(
                itemStatus.preSalesCommission || 0,
                commissionConstants.CURRENCY
            ),
            totalCommissionRenderer: currencySimpleUtility(
                itemStatus.totalCommission || 0,
                commissionConstants.CURRENCY
            ),
        });
        itemStatus.itemsCommission.forEach((itemCommission) => {
            newReportFinanceCommission.push({
                partnerName: '',
                primaryTicketsCount: '',
                secondaryTicketsCount: '',
                preSalesTicketsCount: '',
                totalPrice: '',
                totalPriceCommissioned: '',
                totalPriceRenderer: '',
                partnerNameSplitted: itemCommission.partner || '-',
                primaryCommission: itemCommission.primaryCommission || 0,
                secondaryCommission: itemCommission.secondaryCommission || 0,
                preSalesCommission: itemCommission.preSalesCommission || 0,
                primaryCommissionRenderer: currencySimpleUtility(
                    itemCommission.primaryCommission || 0,
                    commissionConstants.CURRENCY
                ),
                secondaryCommissionRenderer: currencySimpleUtility(
                    itemCommission.secondaryCommission || 0,
                    commissionConstants.CURRENCY
                ),
                preSalesCommissionRenderer: currencySimpleUtility(
                    itemCommission.preSalesCommission || 0,
                    commissionConstants.CURRENCY
                ),
                totalCommissionRenderer: currencySimpleUtility(
                    itemCommission.totalCommission || 0,
                    commissionConstants.CURRENCY
                ),
                totalCommission: itemCommission.totalCommission || 0,
            });
        });
    });

    return newReportFinanceCommission;
}

export function normalizeReportFinanceCommissionListTotals(item) {
    if (!item) return null;

    let newReportFinanceCommissionTotals = [];

    item.forEach((item) => {
        newReportFinanceCommissionTotals.push({
            status: item.status,
            partnerNameSplitted: item.partnerName || ' ',
            totalPriceCommissioned: currencySimpleUtility(
                item.totalPrice || 0,
                commissionConstants.CURRENCY
            ),
            totalCommission: currencySimpleUtility(
                item.totalCommission,
                commissionConstants.CURRENCY
            ),
        });
        item.items.forEach((item) => {
            newReportFinanceCommissionTotals.push({
                status: item.partnerName,
                totalPriceCommissioned: currencySimpleUtility(
                    item.totalPrice || 0,
                    commissionConstants.CURRENCY
                ),
                totalCommission: currencySimpleUtility(
                    item.totalCommission || 0,
                    commissionConstants.CURRENCY
                ),
            });
        });
    });

    return newReportFinanceCommissionTotals;
}

export function normalizeReportEventOrderDetails(data) {
    return {
        amountRenderer: currencySimpleUtility(data.event.totals.price || 0, data.totals.currency),
        amount: data.event.totals.price,
        artistsInfo: data.event.artistsInfo || '-',
        primaryArtist:
            data.event.artistsInfo &&
            Array.isArray(data.event.artistsInfo) &&
            data.event.artistsInfo.length
                ? data.event.artistsInfo[0]
                : '-',
        secondaryArtist:
            data.event.artistsInfo &&
            Array.isArray(data.event.artistsInfo) &&
            data.event.artistsInfo.length > 1
                ? data.event.artistsInfo[1]
                : '-',
        basePriceRenderer: currencySimpleUtility(
            data.event.totals.basePrice || 0,
            data.totals.currency
        ),
        basePrice: data.event.totals.basePrice,
        cashPaymentRenderer: currencySimpleUtility(data.payment.cash || 0, data.totals.currency),
        cashPayment: data.payment.cash,
        channel: data.channel || '-',
        classificationGenre: data.event.classificationGenre || '-',
        classificationGenreId: data.event.classificationGenreId || '-',
        classificationSegment: data.event.classificationSegment || '-',
        classificationSegmentId: data.event.classificationSegmentId || '-',
        classificationSubGenre: data.event.classificationSubGenre || '-',
        classificationSubGenreId: data.event.classificationSubGenreId || '-',
        createdAt: data.createdAt ? dateUtils.dateTimeToEasternTimeFormat(data.createdAt) : '-',
        connexPayOrderNumber: data.payment.virtualCard?.internalRefId || '-',
        currency: data.totals.currency,
        customerCity: data.customer.address.city || '-',
        customerCountry: data.customer.address.countryName || '-',
        customerCountryCode: data.customer.address.countryCode || '-',
        customerPostal: data.customer.address.zipCode || '-',
        customerFirstName: data.customer.firstName || '-',
        customerLastName: data.customer.lastName || '-',
        customerState: data.customer.address.stateCode || '-',
        eventDescription: data.event.description || '-',
        eventName: data.event.name || '-',
        eventOnSaleDate: data.event.onSaleDate
            ? dateTimeLongFormat(dateUtils.parseDateString(data.event.onSaleDate))
            : '-',
        eventStartDate: data.event.startDate
            ? dateTimeLongFormat(dateUtils.parseDateString(data.event.startDate))
            : '-',
        feesRenderer: currencySimpleUtility(data.totals.fees || 0, data.totals.currency),
        fees: data.totals.fees,
        id: data.id,
        loyaltyCurrencyType: data.loyalty.currencyType || '-',
        loyaltyCurrencyName: data.loyalty.currencyName || '-',
        loyaltyBaseCurrency: data.loyalty.baseCurrency || '-',
        loyaltyPointsBurnedRenderer: loyaltyPointsFormat(
            data.loyalty.pointsRedeemed || 0,
            data.loyalty.currencyType,
            data.loyalty.baseCurrency,
            false
        ),
        loyaltyPointsBurned: data.loyalty.pointsRedeemed,
        loyaltyPointsEarnedRenderer: loyaltyPointsFormat(
            data.loyalty.pointsEarned || 0,
            data.loyalty.currencyType,
            data.loyalty.baseCurrency,
            false
        ),
        loyaltyPointsEarned: data.loyalty.pointsEarned,
        loyaltyMemberId: data.customer.loyaltyMembershipNumber || '-',
        loyaltyTierStatus: data.customer.loyaltyTierDescription || '-',
        redemptionItemId: data.loyalty.redemptionItemId || '-',
        loyaltyPartnerTransactionId: data.loyalty.partnerLoyaltyTransactionId || '-',
        gstOrderId: data.id || '-',
        orderNumber: data.event.partnerOrderNumber,
        orderStatus: data.status.toUpperCase(),
        partnerId: data.partnerId,
        partnerName: data.partnerName,
        paymentStatus: data.payment && data.payment.status ? data.payment.status.toUpperCase() : '',
        productId: data.event.productId,
        productTotalRenderer: currencySimpleUtility(
            data.event.totals.productTotal || 0,
            data.event.totals.currency
        ),
        productTotal: data.event.totals.productTotal,
        productType: data.event.productType,
        productTypeId: data.event.productTypeId,
        row: data.event.row,
        seats: data.event.seats,
        section: data.event.section,
        tenantId: data.tenantId,
        tenantName: data.tenantName,
        ticketDetails: ticketDetailsUtility({
            section: data.event.section || null,
            row: data.event.row || null,
            seats: data.event.seats || null,
        }),
        deviceType: data.deviceType || '-',
        ticketQuantity: data.event.totals.quantity || 0,
        ticketType: data.event.ticketType || '-',
        totalAmountRenderer: currencySimpleUtility(
            data.totals.totalPrice || 0,
            data.event.totals.currency
        ),
        totalAmount: data.totals.totalPrice,
        totalTaxesRenderer: currencySimpleUtility(
            data.totals.taxes || 0,
            data.event.totals.currency
        ),
        totalTaxes: data.totals.taxes,
        totalOrders: data.totals.totalPrice || 0,
        transactionId: (data.loyalty.transactionIds && data.loyalty.transactionIds[0]) || '-',
        shippingRenderer: currencySimpleUtility(
            data.event.totals.shipping || 0,
            data.event.totals.currency
        ),
        shipping: data.event.totals.shipping || 0,
        shippingTaxRenderer: currencySimpleUtility(
            data.event.totals.shippingTax || 0,
            data.event.totals.currency
        ),
        shippingTax: data.event.totals.shippingTax,
        venueAddress: data.event.venue.address.line || '-',
        venueCity: data.event.venue.address.city || '-',
        venueCountry: data.event.venue.address.countryCode || '-',
        venueName: data.event.venue.name || '-',
        venueState: data.event.venue.address.stateCode || '-',
    };
}

export function normalizeReportEventOrderDetailsTotals(data) {
    if (!data) return null;

    return data.map((total) => ({
        amount: total.price,
        price: total.price,
        basePrice: total.basePrice,
        currency: total.currency,
        totalOrders: total.totalOrders || 0,
        ticketQuantity: total.ticketQuantity || 0,
        fees: total.fees,
        productTotal: total.productTotal,
        shipping: total.shipping,
        shippingTax: total.shippingTax,
        totalAmount: total.totalPrice,
        totalTaxes: total.totalTaxes,
        cashPayment: total.cashPayment,
        loyaltyBaseCurrency: total.loyaltyBaseCurrency,
        loyaltyCurrencyType: total.loyaltyCurrencyType,
        loyaltyCurrencyName: total.loyaltyCurrencyName,
        loyaltyPointsBurned: total.pointsPayment,
        loyaltyPointsBurnedRenderer: loyaltyPointsFormat(
            total.pointsPayment || 0,
            total.loyaltyCurrencyType,
            total.loyaltyBaseCurrency,
            false
        ),
        loyaltyPointsEarned: total.pointsEarned,
        loyaltyPointsEarnedRenderer: loyaltyPointsFormat(
            total.pointsEarned || 0,
            total.loyaltyCurrencyType,
            total.loyaltyBaseCurrency,
            false
        ),
    }));
}

export function normalizeReportsCustomerActivities(data) {
    return {
        partnerName: data.partnerName,
        createdAt: data.aggregationDate
            ? dateShortFormat(dateUtils.parseDateString(data.aggregationDate))
            : null,
        conversionRenderer: percentFormat(data.conversion),
        conversion: data.conversion,
        customerPurchases: data.customerPurchases,
        customerVisits: data.customerVisits,
        newCustomers: data.newCustomers,
        oldCustomers: data.oldCustomers,
        tenantId: data.tenantId,
    };
}

export function normalizeReportsCustomerActivitiesTotals(data) {
    if (!data) return null;

    return {
        customerVisits: data.customerVisits || 0,
        oldCustomers: data.oldCustomers || 0,
        newCustomers: data.newCustomers || 0,
        customerPurchases: data.customerPurchases || 0,
        conversion: data.conversion ? `${data.conversion}%` : '0%',
    };
}

export function normalizeTopSellingEventsCategories(data) {
    if (!data) return null;

    return {
        text: data.name === 'Undefined' ? 'Other' : data.name,
        value: data.id,
    };
}

export function normalizeTopSellingEventsGenre(data) {
    if (!data) return null;

    return {
        text: data,
        value: data,
    };
}

export function normalizeLocationInfo(data) {
    if (!data) return null;

    return {
        text: data.name,
        value: data.code || data.name,
    };
}

export function normalizeReportOrderEventTable(data) {
    if (!data || !data.detail || !data.detail.items.length) {
        return [];
    }

    const items = data.detail.items;
    const list = [];

    items.forEach((parentItem) => {
        if (parentItem.productTypeId === productsConstants.TYPE.EVENT)
            parentItem.items.forEach((childItem) => {
                list.push({
                    name: parentItem.info.name,
                    description: childItem.description,
                    supplier: parentItem.supplier,
                    quantity: childItem.quantity,
                    basePrice: currencySimpleUtility(childItem.price, data.detail.currency),
                    subTotal: currencySimpleUtility(childItem.totalNetPrice, data.detail.currency),
                    fees: currencySimpleUtility(childItem.fees, data.detail.currency),
                    totalAmount: currencySimpleUtility(childItem.totalPrice, data.detail.currency),
                    section: childItem.section,
                    row: childItem.row || '-',
                    seats: childItem.seats || '-',
                    inventoryType: childItem.inventory || '-',
                    partnerPrice: currencySimpleUtility(
                        childItem.partnerPrice,
                        data.detail.currency
                    ),
                    ticketType: childItem.name,
                    ticketTypeId: childItem.rateId,
                });
            });
    });
    return list;
}

export function normalizeOrderEventTableTotals(data) {
    if (!data || !data.detail || !data.detail.items.length) {
        return {};
    }

    const item = data.detail.items[0];

    if (item.productTypeId === productsConstants.TYPE.EVENT && item.items.length > 1)
        return {
            quantity: item.totalQuantity || 0,
            totalAmount: currencySimpleUtility(item.totalPrice, data.detail.currency),
            subTotal: currencySimpleUtility(item.price, data.detail.currency),
            fees: currencySimpleUtility(item.fees, data.detail.currency),
        };
}

export function normalizeReportOrderHotelReservationTable(data) {
    if (!data || !data.detail || !data.detail.items.length) {
        return [];
    }

    const items = data.detail.items;
    let list = [];

    items.forEach((targetItem) => {
        if (targetItem.productTypeId === productsConstants.TYPE.HOTEL)
            list.push({
                orderPartnerId: targetItem.id,
                packageId: targetItem.packageId || null,
                status: targetItem.status,
                hotelRoomInfo: targetItem.hotelRoomInfo ? targetItem.hotelRoomInfo : '',
                address: targetItem.hotelRoomInfo ? targetItem.hotelRoomInfo.address : '',
                hotelName: targetItem.hotelRoomInfo.hotelName,
                hotelReservationOrderNumber: targetItem.orderNumber || '-',
                tmOrderNumber: targetItem.upsellOrderNumber ?? targetItem.tmOrderNumber ?? '-',
                hotelConfirmationNumber: targetItem.supplierConfirmationNumber,
                roomType: targetItem.hotelRoomInfo.roomType || '-',
                roomId: targetItem.items[0].roomId,
                rateId: targetItem.items[0].rateId,
                supplier: targetItem.supplier,
                roomCount: targetItem.items[0].quantity || 0,
                adultCount: targetItem.items[0].adultCount || 0,
                childCount: targetItem.items[0].childCount || 0,
                childAge: targetItem.items[0].childAge || 0,
                cancellationPolicies: targetItem.hotelRoomInfo.cancellationPolicies || '-',
                mealPlan: targetItem.hotelRoomInfo.mealPlan || '-',
                price: currencySimpleUtility(targetItem.price, data.detail.currency),
                partnerPrice: currencySimpleUtility(
                    targetItem.items[0].partnerPrice || 0,
                    targetItem.partnerCurrency
                ),
                partnerPriceLocal: currencySimpleUtility(
                    targetItem.items[0].partnerPriceLocal || 0,
                    data.detail.currency
                ),
                feesIncluded: currencySimpleUtility(
                    targetItem.feesIncluded || 0,
                    data.detail.currency
                ),
                feesExcluded: currencySimpleUtility(
                    targetItem.feesExcluded || 0,
                    data.detail.currency
                ),
                taxes: currencySimpleUtility(targetItem.fees || 0, data.detail.currency),
                promoCodeApplied: currencySimpleUtility(
                    -targetItem.promoCode.amount || 0,
                    data.detail.currency
                ),
                totalPrice: currencySimpleUtility(targetItem.totalPrice, data.detail.currency),
                payAtPropertyFee: currencySimpleUtility(
                    targetItem.feesExcluded || 0,
                    data.detail.currency
                ),
                currency: data.detail.currency,
                effectiveStartDate: dateUtils.parseDateString(targetItem.stayRange.checkin),
                effectiveStartDateRenderer:
                    dateShortFormat(dateUtils.parseDateString(targetItem.stayRange.checkin)) || '-',
                effectiveEndDate: dateUtils.parseDateString(targetItem.stayRange.checkout),
                effectiveEndDateRenderer:
                    dateShortFormat(dateUtils.parseDateString(targetItem.stayRange.checkout)) ||
                    '-',
                remarks: targetItem.remark || '',
            });
    });

    list.sort(compareOrdersByReservationStatus);
    return list;
}
export function normalizeOfferToken(data) {
    return {
        offerToken: data.offerId || '-',
        offerTokenName: data.name || '-',
        priceRuleset: data.ruleset
            ? {
                  value: data.ruleset.id,
                  text: `${data.ruleset.id} - ${data.ruleset.name}`,
              }
            : null,
        currentCount: data.currentCounter || 0,
        status: data.status || '-',
        offerTokenActive: data.status?.toLowerCase() === 'active',
        createdBy: data.createdBy || '-',
        createdAt: data.createdAt ? dateUtils.parseDateString(data.createdAt) : null,
    };
}
export function normalizeLookupItem(data) {
    return {
        id: data.id,
        name: data.name || '-',
    };
}
